"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.createMultiModifierKey = exports.createHasPropModifierKey = exports.createFilterIsModifierKey = exports.createCustomSelectModifierKey = exports.createCombineFunctionModifierKey = exports.createBCFunctionModifierKey = void 0;
/* eslint-disable no-confusing-arrow */
const ITEM_DELIMITER = '|';
const KEY_VALUE_DELIMITER = ':';
const MODIFIER_NONE = 'NONE';
const KEY_THRESHOLD = 'threshold';
const KEY_FUNCTION = 'function';
const KEY_SUBSET_PROP = 'subsetProp';
const FUNC_ALIGNED = 'aligned';
const FUNC_INTERPOLATED = 'interpolated';
const FUNC_ALIGNED_INTERPOLATED = 'alignedInterpolated';
const FUNC_BELOW_CRITERION_THRESHOLD = 'belowCriterionThreshold';
const FUNC_BELOW_CRITERION_100MV = 'belowCriterion100mv';
const FUNC_CUSTOM_SELECT = 'customSelectedValues';
const FUNC_DECIMATE = 'decimate';
const FUNC_HAS_PROP = 'filterHasProp';
const FUNC_FILTER_IS = 'filterIs';

// @todo - remove this if we end up not neeeding it to be exported
// eslint-disable-next-line no-unused-vars
const FUNCTIONS = {
  // combine functions
  [FUNC_ALIGNED]: FUNC_ALIGNED,
  [FUNC_INTERPOLATED]: FUNC_INTERPOLATED,
  [FUNC_ALIGNED_INTERPOLATED]: FUNC_ALIGNED_INTERPOLATED,
  // bc functions
  [FUNC_BELOW_CRITERION_100MV]: FUNC_BELOW_CRITERION_100MV,
  [FUNC_BELOW_CRITERION_THRESHOLD]: FUNC_BELOW_CRITERION_THRESHOLD,
  // custom select functions
  [FUNC_CUSTOM_SELECT]: FUNC_CUSTOM_SELECT,
  // misc functions
  [FUNC_DECIMATE]: FUNC_DECIMATE,
  [FUNC_HAS_PROP]: FUNC_HAS_PROP,
  [FUNC_FILTER_IS]: FUNC_FILTER_IS
};
const isDefined = v => v !== undefined && v !== null;
const isEmptyString = v => typeof v === 'string' && v.trim().length === 0;
const createKeyValueItem = (key, value) => isDefined(value) && !isEmptyString(value) ? `${key}${KEY_VALUE_DELIMITER}${value}` : undefined;
const createThresholdKeyValue = (lessNegativeThreshold, moreNegativeThreshold) => createKeyValueItem(KEY_THRESHOLD, [isDefined(lessNegativeThreshold) ? `L${lessNegativeThreshold}` : undefined, isDefined(moreNegativeThreshold) ? `M${moreNegativeThreshold}` : undefined].filter(v => isDefined(v)).join('#'));
const createFunctionKeyValue = func => func ? `${KEY_FUNCTION}${KEY_VALUE_DELIMITER}${func}` : undefined;
const selectCombineFunction = _ref => {
  let {
    useAligned = false,
    useInterpolated = false
  } = _ref;
  if (useAligned && useInterpolated) {
    return FUNC_ALIGNED_INTERPOLATED;
  }
  if (useAligned) {
    return FUNC_ALIGNED;
  }
  if (useInterpolated) {
    return FUNC_INTERPOLATED;
  }
  return undefined;
};
const selectBelowCriterionFunction = _ref2 => {
  let {
    useShadow
  } = _ref2;
  if (useShadow) {
    return FUNC_BELOW_CRITERION_100MV;
  }
  return FUNC_BELOW_CRITERION_THRESHOLD;
};
const combineModifierKeyParts = function () {
  let arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const assumedKey = arr.filter(v => !!v).join(ITEM_DELIMITER);
  return assumedKey.length > 0 ? assumedKey : MODIFIER_NONE;
};

// used for combining CIS & Depol to produce a depol dataset aligned and/or interpolated against CIS
const createCombineFunctionModifierKey = _ref3 => {
  let {
    useAligned,
    useInterpolated
  } = _ref3;
  const functionName = selectCombineFunction({
    useAligned,
    useInterpolated
  });
  const functionItem = createFunctionKeyValue(functionName);
  return combineModifierKeyParts([functionItem]);
};

// used for below criterion data sets derived from CIS
exports.createCombineFunctionModifierKey = createCombineFunctionModifierKey;
const createBCFunctionModifierKey = _ref4 => {
  let {
    useShadow,
    lessNegativeThreshold,
    moreNegativeThreshold
  } = _ref4;
  const functionName = selectBelowCriterionFunction({
    useShadow
  });
  const functionItem = createFunctionKeyValue(functionName);
  if (useShadow) {
    return combineModifierKeyParts([functionItem]);
  }
  const thresholdValueItem = createThresholdKeyValue(lessNegativeThreshold, moreNegativeThreshold);
  if (thresholdValueItem) {
    return combineModifierKeyParts([functionItem, thresholdValueItem]);
  }
  return MODIFIER_NONE;
};

// used for hasProp
exports.createBCFunctionModifierKey = createBCFunctionModifierKey;
const createHasPropModifierKey = _ref5 => {
  let {
    prop
  } = _ref5;
  if (isDefined(prop)) {
    const functionItem = createFunctionKeyValue(FUNC_HAS_PROP);
    const valueItem = `prop:${prop.toLowerCase()}`;
    return combineModifierKeyParts([functionItem, valueItem]);
  }
  return MODIFIER_NONE;
};

// used for filterIs
exports.createHasPropModifierKey = createHasPropModifierKey;
const createFilterIsModifierKey = _ref6 => {
  let {
    keys
  } = _ref6;
  if (isDefined(keys)) {
    const functionItem = createFunctionKeyValue(FUNC_FILTER_IS);
    const _keys = (keys instanceof Array ? keys : [keys]).filter(v => !!v);
    _keys.sort();
    if (_keys.length) {
      const valueItem = `keys:${_keys.map(k => k.toLowerCase()).join(',')}`;
      return combineModifierKeyParts([functionItem, valueItem]);
    }
  }
  return MODIFIER_NONE;
};

// used for user selected readings of some CIS dataset
// subset prop should be used if only selecting subset values, leave undefined if not needed.
exports.createFilterIsModifierKey = createFilterIsModifierKey;
const createCustomSelectModifierKey = function () {
  let {
    subsetProp
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const functionName = FUNC_CUSTOM_SELECT;
  const functionItem = createFunctionKeyValue(functionName);
  const keyValueItem = createKeyValueItem(KEY_SUBSET_PROP, subsetProp);
  const parts = [functionItem, keyValueItem].filter(v => isDefined(v));
  return combineModifierKeyParts(parts);
};
exports.createCustomSelectModifierKey = createCustomSelectModifierKey;
const createDecimateFunctionModifierKey = _ref7 => {
  let {
    decimate
  } = _ref7;
  if (decimate) {
    return createFunctionKeyValue(FUNC_DECIMATE);
  }
  return MODIFIER_NONE;
};
const createMultiModifierKey = _ref8 => {
  let {
    alignmentFlags = {},
    belowCriterionFlags = {},
    decimateFlags = {},
    hasPropFlags = {},
    filterIsFlags = {}
  } = _ref8;
  // MULTI KEYS
  const keys = {
    alignment: undefined,
    belowCriterion: undefined,
    decimate: undefined,
    hasProp: undefined,
    filterIs: undefined
  };

  // ALIGNMENT MODIFIER
  const {
    useAligned = false,
    useInterpolated = false
  } = alignmentFlags;
  keys.alignment = createCombineFunctionModifierKey({
    useAligned,
    useInterpolated
  });

  // BELOW CRITERION MODIFIER
  const {
    useShadow = false,
    useThreshold = false,
    lessNegativeThreshold,
    moreNegativeThresholdhold
  } = belowCriterionFlags;
  keys.belowCriterion = createBCFunctionModifierKey({
    useShadow,
    lessNegativeThreshold: useThreshold && isDefined(lessNegativeThreshold) ? lessNegativeThreshold : undefined,
    moreNegativeThresholdhold: useThreshold && isDefined(moreNegativeThresholdhold) ? moreNegativeThresholdhold : undefined
  });

  // FILTER HAS PROP
  const {
    prop
  } = hasPropFlags;
  if (isDefined(prop)) {
    keys.hasProp = createHasPropModifierKey({
      prop
    });
  }

  // FILTER IS
  const filterIsKeys = Object.keys(filterIsFlags);
  if (isDefined(filterIsKeys) && filterIsKeys.length) {
    keys.filterIs = createFilterIsModifierKey({
      keys: filterIsKeys
    });
  }

  // DECIMATE MODIFIER
  const {
    decimate
  } = decimateFlags;
  keys.decimate = createDecimateFunctionModifierKey({
    decimate
  });

  // COMBINE ALL KEYS
  const arrKeyParts = [keys.alignment, keys.belowCriterion, keys.hasProp, keys.filterIs, keys.decimate].filter(v => !!v && v !== MODIFIER_NONE);
  const modifierKey = combineModifierKeyParts(arrKeyParts);

  // RESULT
  return modifierKey || MODIFIER_NONE;
};
exports.createMultiModifierKey = createMultiModifierKey;
var _default = {
  createCombineFunctionModifierKey,
  createBCFunctionModifierKey,
  createHasPropModifierKey,
  createFilterIsModifierKey,
  createCustomSelectModifierKey,
  createMultiModifierKey
};
exports.default = _default;